import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse } from "axios";
import { RootState } from "../store";
import axiosInstance from "src/apis/axiosInstance";
import {
  APIResponseCover,
  Pagination,
  PartnerAPIResponseCover,
} from "./auth.d";
import { readAPIErrorMessage, throwErrApi } from "src/utils/errorUtil";
import { API_STATUS, DATA_LIMIT } from "src/utils/constants";

export interface AirlineDataForm {
  airline_name: string;
  airline_code: string;
  contact_number: string;
  contact_person_name: string;
  email_id: string;
}

export interface AirlineEditDataForm extends AirlineDataForm {
  id: string;
}

export interface AirlineData {
  id: string;
  airline_name: string;
  airline_code: string;
  contact_number: string;
  contact_person_name: string;
  email_id: string;
  status: number;
  created_date: string;
  updated_date: string;
}

export interface AirlinesState {
  data?: Pagination<AirlineData>;
  status: API_STATUS;
  list: {
    data: AirlineData[];
    status: API_STATUS;
  };
  view: {
    data?: AirlineData;
    status: API_STATUS;
  };
}

const initialState: AirlinesState = {
  data: {
    count: 0,
    next: null,
    previous: null,
    results: [],
  },
  list: {
    data: [],
    status: "idle",
  },
  view: {
    data: undefined,
    status: "idle",
  },
  status: "idle",
};

export const airlinesSlice = createSlice({
  name: "airlines",
  initialState,
  reducers: {
    refreshAirlineList: (state) => {
      state.status = "idle";
    },
    resetAirlineView(state) {
      state.view.status = "idle";
    },
  },
  extraReducers(builder) {
    builder.addCase(fetchAirlines.pending, (state, action) => {
      state.status = "pending";
    });
    builder.addCase(fetchAirlines.fulfilled, (state, action) => {
      state.status = "success";
      state.data = action.payload;
    });
    builder.addCase(fetchAirlines.rejected, (state, action) => {
      state.status = "error";
    });
    builder.addCase(fetchListAirlines.pending, (state, action) => {
      state.list.status = "pending";
    });
    builder.addCase(fetchListAirlines.fulfilled, (state, action) => {
      state.list.status = "success";
      state.list.data = action.payload;
    });
    builder.addCase(fetchListAirlines.rejected, (state, action) => {
      state.list.status = "error";
    });
    builder.addCase(addAirline.fulfilled, (state, action) => {
      state.status = "idle";
    });
    builder.addCase(viewAirline.fulfilled, (state, action) => {
      state.view.data = action.payload;
      state.view.status = "success";
    });
    builder.addCase(viewAirline.pending, (state, action) => {
      state.view.status = "pending";
    });
    builder.addCase(viewAirline.rejected, (state, action) => {
      state.view.status = "error";
    });
  },
});

export const fetchAirlines = createAsyncThunk<
  Pagination<AirlineData>,
  { page: number; keyword: string },
  { state: RootState }
>("airline/fetch", async ({ page, keyword }, thunkAPI) => {
  try {
    const response = await axiosInstance.get<
      PartnerAPIResponseCover<Pagination<AirlineData>>,
      AxiosResponse<PartnerAPIResponseCover<Pagination<AirlineData>>>
    >(
      `partners/airline?limit=${DATA_LIMIT}&keyword=${keyword}&offset=${
        DATA_LIMIT * (page - 1)
      }&format=json`
    );
    return thunkAPI.fulfillWithValue(response.data.data);
  } catch (e: unknown) {
    if (e instanceof AxiosError) {
      throw new Error(readAPIErrorMessage(e));
    } else if (e instanceof Error) {
      throw new Error(e.message);
    }
    return thunkAPI.rejectWithValue(false);
  }
});

export const fetchListAirlines = createAsyncThunk<
  AirlineData[],
  void,
  { state: RootState }
>("airlines/fetchList", async (_, thunkAPI) => {
  try {
    const response = await axiosInstance.get<
      PartnerAPIResponseCover<AirlineData[]>,
      AxiosResponse<PartnerAPIResponseCover<AirlineData[]>>
    >(`partners/airline/search/?format=json`);
    return thunkAPI.fulfillWithValue(response.data.data);
  } catch (e: unknown) {
    if (e instanceof AxiosError) {
      throw new Error(readAPIErrorMessage(e));
    } else if (e instanceof Error) {
      throw new Error(e.message);
    }
    return thunkAPI.rejectWithValue(false);
  }
});

export const deleteAirline = createAsyncThunk<
  { status: string },
  string,
  { state: RootState }
>("airline/delete", async (id, thunkAPI) => {
  try {
    const response = await axiosInstance.delete<
      APIResponseCover<Record<never, never>>,
      AxiosResponse<APIResponseCover<Record<never, never>>>
    >(`partners/airline/${id}?format=json`);
    return thunkAPI.fulfillWithValue({ status: "idle" });
  } catch (e) {
    if (e instanceof AxiosError) {
      throwErrApi(e);
    } else if (e instanceof Error) {
      throw new Error(e.message);
    }
    return thunkAPI.rejectWithValue(false);
  }
});

export const updateAirline = createAsyncThunk<
  { status: string },
  AirlineEditDataForm,
  { state: RootState }
>("airline/update", async (data: AirlineEditDataForm, thunkAPI) => {
  try {
    const response = await axiosInstance.put<
      APIResponseCover<Record<never, never>>,
      AxiosResponse<APIResponseCover<Record<never, never>>, AirlineEditDataForm>,
      AirlineEditDataForm
    >(`partners/airline/${data.id}?format=json`, {
      ...data,
    });
    return thunkAPI.fulfillWithValue({ status: "pending" });
  } catch (e) {
    if (e instanceof AxiosError) {
      throwErrApi(e);
    } else if (e instanceof Error) {
      throw new Error(e.message);
    }
    return thunkAPI.rejectWithValue(false);
  }
});

export const viewAirline = createAsyncThunk<
  AirlineData,
  string,
  { state: RootState }
>("airline/view", async (id, thunkAPI) => {
  try {
    const response = await axiosInstance.get<
      APIResponseCover<AirlineData>,
      AxiosResponse<APIResponseCover<AirlineData>>
    >(`partners/airline/${id}?format=json`);
    return response.data.data;
  } catch (e) {
    if (e instanceof AxiosError) {
      throw new Error(readAPIErrorMessage(e));
    } else if (e instanceof Error) {
      throw new Error(e.message);
    }
    return thunkAPI.rejectWithValue(false);
  }
});

export const addAirline = createAsyncThunk<
  { status: string },
  AirlineDataForm,
  { state: RootState }
>("airline/add", async (data: AirlineDataForm, thunkAPI) => {
  try {
    const response = await axiosInstance.post<
      APIResponseCover<Record<never, never>>,
      AxiosResponse<APIResponseCover<Record<never, never>>, AirlineDataForm>,
      AirlineDataForm
    >("partners/airline?format=json", data);
    return thunkAPI.fulfillWithValue({ status: "idle" });
  } catch (e: unknown) {
    if (e instanceof AxiosError) {
      throwErrApi(e);
    } else if (e instanceof Error) {
      throw new Error(e.message);
    }
    return thunkAPI.rejectWithValue(false);
  }
});

export const { refreshAirlineList, resetAirlineView } = airlinesSlice.actions;

export default airlinesSlice.reducer;
